import { HttpClient } from '@/shared/infra/http/HttpClient';
import { ITncRepository } from '../../application/ports/ITncRepository';
import {
  TncCase,
  SignatureData,
  CaseClosureData,
  TncUpdatePayload,
} from '../../application/domain/entities';
import { EmployeeDecision, TncStep } from '../../application/domain/enums';
import { mapTncPayloadToDto, mapTncResponseDtoToCase } from './mappers/tncMapper';
import { TncDetailsResponseDto } from './dto/res/TncDetailsResponseDto';

export class ApiTncRepository implements ITncRepository {
  constructor(private readonly _httpClient: HttpClient) {}

  // ─── Primary — GET ────────────────────────────────────────────────────────────

  /**
   * GET /e-verify/tnc-details/{caseId}
   * Fetches the full TNC process state for a given E-Verify case.
   */
  async getTncCase(caseId: string): Promise<TncCase> {
    const response = await this._httpClient.get<TncDetailsResponseDto>(
      `/e-verify/tnc-details/${caseId}`
    );
    return mapTncResponseDtoToCase(response.data);
  }

  // ─── Primary — PUT ────────────────────────────────────────────────────────────

  /**
   * PUT /e-verify/tnc-details/{caseId}
   * Called from every step's Next handler with a partial payload.
   * The server merges new fields with previously saved data.
   */
  async updateTncDetails(caseId: string, payload: TncUpdatePayload): Promise<void> {
    const dto = mapTncPayloadToDto(payload);
    await this._httpClient.put(`/e-verify/tnc-details/${caseId}`, dto);
  }

  /**
   * POST /e-verify/tnc-details/fan-download/{caseId}
   * Returns base64 encoded PDF content for the FAN document.
   */
  async downloadFan(caseId: string, blobUrl: string): Promise<string> {
    const response = await this._httpClient.post<{ document: string }>(
      `/e-verify/tnc-details/fan-download/${caseId}`,
      { blobUrl, isFanDownload: true }
    );
    return response.data.document;
  }

  // ─── Legacy stubs (not yet integrated — kept for interface compliance) ─────────

  async updateCaseDetails(caseId: string, _details: object): Promise<TncCase> {
    // TODO: Wire up when endpoint is available
    return this.getTncCase(caseId);
  }

  async startTncProcess(caseId: string): Promise<TncCase> {
    // TODO: Wire up when endpoint is available
    const existingCase = await this.getTncCase(caseId);
    return { ...existingCase, currentStep: TncStep.EMPLOYEE_AVAILABILITY };
  }

  async updateEmployeeAvailability(caseId: string, isPresent: boolean): Promise<TncCase> {
    // TODO: Wire up when endpoint is available
    const existingCase = await this.getTncCase(caseId);
    return {
      ...existingCase,
      employeePresent: isPresent,
      currentStep: isPresent ? TncStep.TNC_INSTRUCTIONS : TncStep.EMAIL_FAN,
    };
  }

  async sendFanDocument(caseId: string, _method: 'email' | 'link'): Promise<TncCase> {
    // TODO: Wire up when endpoint is available
    const existingCase = await this.getTncCase(caseId);
    return { ...existingCase, currentStep: TncStep.TNC_INSTRUCTIONS };
  }

  async acknowledgeTncInstructions(caseId: string): Promise<TncCase> {
    // TODO: Wire up when endpoint is available
    const existingCase = await this.getTncCase(caseId);
    return { ...existingCase, currentStep: TncStep.EMPLOYEE_DECISION };
  }

  async recordEmployeeDecision(
    caseId: string,
    decision: EmployeeDecision,
    _deadline?: string
  ): Promise<TncCase> {
    // TODO: Wire up when endpoint is available
    const existingCase = await this.getTncCase(caseId);
    return {
      ...existingCase,
      employeeDecision: decision,
      currentStep: TncStep.SIGNATURE,
    };
  }

  async submitSignatures(
    caseId: string,
    employeeSignature: SignatureData,
    employerSignature: SignatureData,
    _declined?: boolean
  ): Promise<TncCase> {
    // TODO: Wire up when endpoint is available
    const existingCase = await this.getTncCase(caseId);
    return {
      ...existingCase,
      employeeSignature,
      employerSignature,
      currentStep: TncStep.DOWNLOAD_RDC,
    };
  }

  async downloadRdc(_caseId: string): Promise<Blob> {
    // TODO: Wire up when endpoint is available
    return new Blob(['RDC Content'], { type: 'application/pdf' });
  }

  async closeCase(caseId: string, closureData: CaseClosureData): Promise<void> {
    // TODO: Wire up to close case endpoint when available
    console.log('Closing case:', caseId, closureData);
  }
}
